import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import ReCAPTCHA from 'react-google-recaptcha';
import Tooltip from '../reusable/Tooltip'; 
import ForgotPasswordModal from '../modals/ForgotPasswordModal';
import AlertModal from '../reusable/AlertModal';

const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [globalAlert, setGlobalAlert] = useState({ isOpen: false, title: "", message: "" });

  const [formData, setFormData] = useState({
    identifier: "", 
    password: ""
  });

  /*useEffect(() => {
    const token = localStorage.getItem('capstonne_access_token');
    if (token) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);*/
  // --- VALIDATION ENGINE ---
  const validateField = (name, value) => {
    if (value.trim() === "") return "";
    let errorMsg = "";

    switch (name) {
      case "identifier":
        if (!/^[6-9]\d{9}$/.test(value)) {
          errorMsg = "Enter a valid 10-digit registered mobile number.";
        }
        break;
      case "password":
        if (value.length < 8) {
          errorMsg = "Password must be at least 8 characters.";
        }
        break;
      default:
        break;
    }
    return errorMsg;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    const fieldError = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: fieldError }));
  };

  const validateAll = () => {
    let newErrors = {};
    
    if (formData.identifier.trim() === "") newErrors.identifier = "Mobile number is required.";
    else {
      const idError = validateField("identifier", formData.identifier);
      if (idError) newErrors.identifier = idError;
    }

    if (formData.password.trim() === "") newErrors.password = "Password is required.";
    
    if (!recaptchaToken) newErrors.recaptcha = "Please verify that you are a human.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // --- API CALL ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");

    if (validateAll()) {
      setLoading(true);
      try {
        const payload = {
          identifier: formData.identifier, 
          password: formData.password
        };

        const response = await axiosInstance.post('/auth/login', payload);
        console.log("call successful");

        if (response.status === 200 || response.status === 201) {
          const { accessToken, refreshToken, role, privilege } = response.data;
          
          localStorage.setItem('capstonne_access_token', accessToken);
          if (refreshToken) localStorage.setItem('capstonne_refresh_token', refreshToken);
        
          // Look for role or privilege directly from the API response
          const finalRole = (role || privilege || 'user').toLowerCase();
        
          if (finalRole === 'admin') {
            navigate('/dashboard/admin', { replace: true });
          } else {
            navigate('/dashboard/userdashboard', { replace: true });
          }
        }
      } catch (err) {
        console.log("LOGIN ERROR:", err.response?.data);
        const status = err.response?.status;
        const msg = err.response?.data?.message || err.response?.data?.error || "Invalid credentials or server error.";
        
        // --- THE SMART ERROR ROUTER ---
        const lowerMsg = msg.toLowerCase();

        if (
          lowerMsg.includes("rejected") 
        ) {
          setGlobalAlert({
            isOpen: true,
            title: "Registration Rejected",
            message: msg // Passes the exact backend message to the modal!
          });
          setApiError(""); // Clear any standard text errors
        }
        // 1. Pending Admin Approval
       else if (
          lowerMsg.includes("rev") || 
          lowerMsg.includes("pending") || 
          lowerMsg.includes("approv") || 
          lowerMsg.includes("inactive") ||
          status === 403 // Sometimes backends use 403 Forbidden for unapproved accounts
        ) {
          setGlobalAlert({
            isOpen: true,
            title: "Account Under Review",
            message: "Your registration is currently pending admin approval. You will be able to log in once your account is activated."
          });
          setApiError(""); 
        } 
         
        // 2. Rate Limiting / Security Lockouts (Removed 'wait' and 'block' to prevent false positives)
        else if (
          status === 429 || 
          lowerMsg.includes("limit") || 
          lowerMsg.includes("too many") || 
          lowerMsg.includes("lockout")
        ) {
          setGlobalAlert({
            isOpen: true,
            title: "Access Blocked",
            message: "Too many failed attempts. Please try again after some time to protect your account security."
          });
          setApiError(""); 
        } 
        
        // 3. Normal Errors (Invalid password, user not found, etc.)
        else {
          setApiError(msg);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    if (token) {
      setErrors(prev => ({ ...prev, recaptcha: "" }));
    }
  };

  const getRegisterLink = () => {
    const savedCode = sessionStorage.getItem('pending_referral');
    return savedCode ? `/register/${savedCode}` : "/register";
  };

  return (
    <section className="min-h-screen bg-gray-50 p-4 md:p-8 flex items-center justify-center font-sans text-gray-800">
      {/* Boxy main container (rounded-lg for a sharp, crisp outer edge) */}
      <div className="max-w-6xl w-full bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden flex flex-col lg:flex-row">
        
        {/* Left Column: MLM Theme (Indigo/Slate) */}
        <div className="hidden lg:flex lg:w-5/12 bg-gradient-to-br from-indigo-600 via-indigo-700 to-slate-900 text-white p-10 md:p-16 flex-col justify-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white opacity-5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full translate-x-1/3 translate-y-1/3"></div>
          
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-6 leading-tight">
              Welcome Back <br/> to Capstonne
            </h2>
            <p className="text-indigo-100 mb-8 text-lg">
              Log in to manage your network, track real-time commissions, and grow your downline seamlessly.
            </p>
            <ul className="space-y-4 text-sm font-medium text-indigo-50">
              <li className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shadow-inner">✓</span>
                Access your Dashboard
              </li>
              <li className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shadow-inner">✓</span>
                Monitor your Payout Wallet
              </li>
            </ul>
          </div>
        </div>

        {/* Right Column: Form */}
        <div className="w-full lg:w-7/12 flex items-center justify-center p-8 sm:p-10 lg:p-14">
          
          <div className="w-full max-w-md">
            
            <div className="mb-10 text-center sm:text-left">
              <h3 className="text-3xl font-extrabold text-gray-900 mb-2 tracking-tight">Sign In</h3>
              <p className="text-gray-500 text-sm font-medium">Enter your credentials to access your account.</p>
            </div>

            <form onSubmit={handleSubmit} noValidate className="space-y-6">
              
              {/* Row 1: Contact / Identifier (Boxy + Perfect Border Slide) */}
              <div> 
                <div className="relative group">
                  <input 
                    required 
                    type="tel" 
                    id="identifier" 
                    name="identifier" 
                    value={formData.identifier} 
                    onChange={(e) => handleChange({ target: { name: 'identifier', value: e.target.value.replace(/\D/g, '') }})} 
                    placeholder="e.g. 98765 43210" 
                    maxLength="10" 
                    className={`peer w-full rounded-md border bg-transparent px-4 py-3.5 pl-[4.5rem] text-gray-900 outline-none transition-all placeholder-transparent focus:placeholder-gray-400 focus:ring-1 ${errors.identifier ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-indigo-600 focus:ring-indigo-600'}`} 
                    autoComplete="tel-national" 
                  />
                  <span className="pointer-events-none absolute left-10 top-1/2 -translate-y-1/2 text-gray-500 font-medium opacity-0 transition-opacity duration-200 peer-focus:opacity-100 peer-[&:not(:placeholder-shown)]:opacity-100">+91</span>
                  
                  {/* The Slide-Up Label: Uses top-0 and -translate-y-1/2 to perfectly intersect the border */}
                  <label 
                    htmlFor="identifier" 
                    className={`pointer-events-none absolute left-9 transition-all duration-200 px-1 bg-white
                      ${(formData.identifier || errors.identifier) ? 'top-0 -translate-y-1/2 text-xs' : 'top-1/2 -translate-y-1/2 text-base'} 
                      peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-xs
                      ${errors.identifier ? 'text-red-500' : 'text-gray-500 peer-focus:text-indigo-600'}`}
                  >
                    Mobile Number *
                  </label>
                  
                  <svg className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors ${errors.identifier ? 'text-red-400' : 'text-gray-400 peer-focus:text-indigo-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                </div>
                {errors.identifier && <p className="text-red-500 text-xs mt-1.5 ml-1 font-medium">{errors.identifier}</p>}
              </div>

              {/* Row 2: Password (Boxy + Perfect Border Slide) */}
              <div>
                <div className="relative group">
                  <input 
                    required 
                    type={showPassword ? "text" : "password"} 
                    id="password" 
                    name="password" 
                    value={formData.password} 
                    onChange={handleChange} 
                    placeholder="Enter your password" 
                    className={`peer w-full rounded-md border bg-transparent px-4 py-3.5 pl-10 pr-12 text-gray-900 outline-none transition-all placeholder-transparent focus:placeholder-gray-400 focus:ring-1 
                      ${errors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-indigo-600 focus:ring-indigo-600'}`} 
                    autoComplete="current-password" 
                  />
                  
                  {/* The Slide-Up Label */}
                  <label 
                    htmlFor="password" 
                    className={`pointer-events-none absolute left-9 transition-all duration-200 px-1 bg-white
                      ${(formData.password || errors.password) ? 'top-0 -translate-y-1/2 text-xs' : 'top-1/2 -translate-y-1/2 text-base'} 
                      peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-xs
                      ${errors.password ? 'text-red-500' : 'text-gray-500 peer-focus:text-indigo-600'}`}
                  >
                    Password *
                  </label>

                  <svg className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors ${errors.password ? 'text-red-400' : 'text-gray-400 peer-focus:text-indigo-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                  </svg>
                  
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-indigo-600 focus:outline-none">
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88L1 1m11 11L22 22"></path></svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
                    )}
                  </button>
                </div>
                
                {/* Error and Forgot Password Row positioned perfectly underneath */}
                <div className="flex items-start justify-between mt-2">
                  <div className="flex-1 pr-2">
                    {errors.password && <p className="text-red-500 text-xs font-medium ml-1">{errors.password}</p>}
                  </div>
                  <button type="button" onClick={() => setShowForgotModal(true)} className="text-sm font-bold text-indigo-600 hover:text-indigo-800 transition-colors outline-none focus:underline whitespace-nowrap pt-1">
                    Forgot password?
                  </button>
                </div>
              </div>

              {/* Google reCAPTCHA (Centered) */}
              <div className="flex flex-col items-center justify-center py-2">
                <div className="transform scale-[0.90] sm:scale-100 origin-center">
                  <ReCAPTCHA
                    sitekey="6LezKLosAAAAACH9IEVqwOgcymgGbVe_nm_uCuP_" // Remember to replace this! localhost 6LdOKYssAAAAAAHhRcHwRNcujxbQCuy1RfEO6SyS, webasite 6LezKLosAAAAACH9IEVqwOgcymgGbVe_nm_uCuP_
                    onChange={handleRecaptchaChange}
                  />
                </div>
                {errors.recaptcha && <p className="mt-2 text-xs text-red-500 font-medium text-center">{errors.recaptcha}</p>}
              </div>

              {/* Submit Button & Errors */}
              <div className="pt-2">
                {apiError && (
                  <div className="mb-6 p-4 rounded-md flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 animate-in fade-in slide-in-from-top-2 duration-300">
                    <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-sm font-bold">Login Failed</p>
                      <p className="text-xs opacity-90 mt-0.5">{apiError}</p>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-3.5 rounded-md font-bold text-sm transition-all transform active:scale-[0.98] shadow-sm
                    ${loading 
                      ? 'bg-indigo-400 cursor-not-allowed text-white' 
                      : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 mr-3 text-white" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Authenticating...
                    </div>
                  ) : (
                    "Secure Login"
                  )}
                </button>
              </div>

              {/* Registration Link */}
              <div className="text-center mt-6">
                <p className="text-sm text-gray-600 font-medium">
                  Don't have an account?{' '}
                  <Link to={getRegisterLink()} className="font-bold text-indigo-600 hover:text-indigo-800 transition-colors">
                    Create one now
                  </Link>
                </p>
              </div>

            </form>
          </div>
        </div>
      </div>
      
      {/* Modals */}
      <ForgotPasswordModal 
        isOpen={showForgotModal} 
        onClose={() => setShowForgotModal(false)} 
      />
      <AlertModal 
        isOpen={globalAlert.isOpen}
        title={globalAlert.title}
        message={globalAlert.message}
        onClose={() => setGlobalAlert({ ...globalAlert, isOpen: false })}
      />
    
    </section>
  );
};

export default Login;